import { StandardEvents } from '@shopify/events';

/**
 * Magento-parity PDP circular badge (new / sale / travel).
 * New tag wins; otherwise sale/travel follow the selected variant.
 */
class MgPdpProductBadge extends HTMLElement {
  /** @type {Record<string, { onSale?: boolean, travel?: string | null }>} */
  #variants = {};

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    const jsonEl = this.querySelector('[data-mg-pdp-badge-variants]');
    if (jsonEl?.textContent) {
      try {
        this.#variants = JSON.parse(jsonEl.textContent);
      } catch {
        this.#variants = {};
      }
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#onProductSelect,
      { signal }
    );

    this.#applyVariant(this.dataset.initialVariantId || '');
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const syncId = /** @type {{ detail?: { variantId?: string } }} */ (event).detail?.variantId;
    if (syncId) this.#applyVariant(String(syncId));

    const productSelectEvent = /** @type {{ promise?: Promise<{ detail?: { resource?: { id?: string | number } } }> }} */ (
      event
    );

    if (!(productSelectEvent.promise instanceof Promise)) return;

    productSelectEvent.promise
      .then(({ detail }) => {
        const id = detail?.resource?.id;
        if (id != null) this.#applyVariant(String(id));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[mg-pdp-product-badge] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {string} variantId
   */
  #applyVariant(variantId) {
    const labelEl = this.querySelector('[data-mg-pdp-badge-label]');
    if (!(labelEl instanceof HTMLElement)) return;

    if (this.dataset.enableNew === 'true' && this.dataset.hasNew === 'true') {
      this.#show('new', this.dataset.newLabel || 'new.');
      return;
    }

    const data = this.#variants[variantId];
    let type = '';
    let label = '';

    if (this.dataset.enableSale === 'true' && data?.onSale) {
      type = 'sale';
      label = this.dataset.saleLabel || 'sale.';
    } else if (this.dataset.enableTravel === 'true') {
      const travel = typeof data?.travel === 'string' ? data.travel.trim() : '';
      if (travel) {
        type = 'travel';
        label = travel;
      }
    }

    if (!type) {
      this.hidden = true;
      labelEl.textContent = '';
      this.classList.remove('mg-product-badge--sale', 'mg-product-badge--travel', 'mg-product-badge--new');
      return;
    }

    this.#show(type, label);
  }

  /**
   * @param {string} type
   * @param {string} label
   */
  #show(type, label) {
    const labelEl = this.querySelector('[data-mg-pdp-badge-label]');
    if (labelEl instanceof HTMLElement) labelEl.textContent = label;

    this.classList.toggle('mg-product-badge--new', type === 'new');
    this.classList.toggle('mg-product-badge--sale', type === 'sale');
    this.classList.toggle('mg-product-badge--travel', type === 'travel');
    this.hidden = false;
  }
}

if (!customElements.get('mg-pdp-product-badge')) {
  customElements.define('mg-pdp-product-badge', MgPdpProductBadge);
}
